import {
  Directive,
  EffectCleanupRegisterFn,
  effect,
  input,
  signal,
  untracked,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs';

@Directive({
  selector: 'form[formGroup][xFormChangeTracker]',
  standalone: true,
  exportAs: 'xFormChangeTracker',
})
export class FormChangeTrackerDirective {
  public readonly formGroup = input.required<FormGroup>({
    alias: 'formGroup',
  });
  public readonly debounceMs = input<number>(200, { alias: 'debounceMs' });

  public readonly $isFormChanged = signal(false);

  private readonly initialComparableFormValue = signal<string>(
    JSON.stringify(null),
  );

  constructor() {
    effect(onCleanup => this.trackFormChanges(onCleanup));
  }

  private trackFormChanges(onCleanup: EffectCleanupRegisterFn): void {
    const form = this.formGroup();

    if (!form) {
      return;
    }

    this.resetTrackingState(form);

    const subscription = form.valueChanges
      .pipe(debounceTime(this.debounceMs()))
      .subscribe(() => {
        this.updateTrackingState(form);
      });

    onCleanup(() => {
      subscription.unsubscribe();
    });
  }

  private resetTrackingState(form: FormGroup): void {
    this.initialComparableFormValue.set(
      this.serializeComparableFormValue(form),
    );
    this.$isFormChanged.set(false);
  }

  private updateTrackingState(form: FormGroup): void {
    // Programmatic updates during initialization keep form pristine, so we refresh baseline.
    if (form.pristine) {
      this.resetTrackingState(form);

      return;
    }

    const currentComparableValue = this.serializeComparableFormValue(form);
    const initialComparableValue = untracked(() =>
      this.initialComparableFormValue(),
    );

    this.$isFormChanged.set(initialComparableValue !== currentComparableValue);
  }

  private serializeComparableFormValue(form: FormGroup): string {
    return JSON.stringify(this.toComparableValue(form.getRawValue()));
  }

  private toComparableValue(value: unknown): unknown {
    if (value instanceof Date) {
      return value.toISOString();
    }

    if (value instanceof File) {
      return this.toComparableFileValue(value);
    }

    if (Array.isArray(value)) {
      return value.map(item => this.toComparableValue(item));
    }

    if (value !== null && typeof value === 'object') {
      const objectValue = value as Record<string, unknown>;
      const normalizedValue: Record<string, unknown> = {};

      Object.keys(objectValue)
        .sort()
        .forEach(key => {
          normalizedValue[key] = this.toComparableValue(objectValue[key]);
        });

      return normalizedValue;
    }

    return value ?? null;
  }

  private toComparableFileValue(file: File): Record<string, unknown> {
    const extendedFile = file as File & Record<string, unknown>;

    return {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      id: extendedFile['id'] ?? null,
      path: extendedFile['path'] ?? null,
      uploadedFilename: extendedFile['uploadedFilename'] ?? null,
      isExisting: extendedFile['isExisting'] ?? null,
      previewUrl: extendedFile['previewUrl'] ?? null,
    };
  }
}
